import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Field } from '@promtver/ui-kit';

import { getEnterprisesForModeration, getPassportTemplates, savePassportTemplate } from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { downloadCsv, type CsvCell } from '../../lib/csv';
import { categoryLabel } from '../../lib/format';
import type { Enterprise } from '@promtver/contracts';
import type { PassportField, PassportTemplate } from '../../api/types';

const TEMPLATE_STORAGE_KEY = 'promtver.admin.passport-template.v1';

const DEFAULT_FIELDS: PassportField[] = [
  { id: 'f-name', source: 'name', label: ru.registry.defaultFields.name },
  { id: 'f-legal', source: 'legalName', label: ru.registry.defaultFields.legalName },
  { id: 'f-district', source: 'districtName', label: ru.registry.defaultFields.districtName },
  { id: 'f-address', source: 'address', label: ru.registry.defaultFields.address },
  { id: 'f-category', source: 'category', label: ru.registry.defaultFields.category },
  { id: 'f-founded', source: 'foundedYear', label: ru.registry.defaultFields.foundedYear },
  { id: 'f-description', source: 'description', label: ru.registry.defaultFields.description },
  { id: 'f-phone', source: 'contactPhone', label: ru.registry.defaultFields.contactPhone },
  { id: 'f-email', source: 'contactEmail', label: ru.registry.defaultFields.contactEmail },
  { id: 'f-site', source: 'website', label: ru.registry.defaultFields.website },
];

function readLocalTemplate(): PassportTemplate {
  try {
    const raw = window.localStorage.getItem(TEMPLATE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PassportTemplate;
      if (parsed?.fields?.length) return parsed;
    }
  } catch {
    // localStorage недоступен — используется шаблон по умолчанию.
  }
  return { id: 'default', name: ru.registry.defaultTemplateName, fields: DEFAULT_FIELDS };
}

function writeLocalTemplate(template: PassportTemplate): void {
  try {
    window.localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(template));
  } catch {
    // Сохранение не удалось — шаблон останется только в состоянии компонента.
  }
}

function readFieldValue(enterprise: Enterprise, source: string): string {
  const raw = (enterprise as unknown as Record<string, unknown>)[source];
  if (raw === null || raw === undefined || raw === '') return '';
  if (source === 'category') return categoryLabel(String(raw));
  return String(raw);
}

/**
 * Готовность карточек к включению в государственный реестр (FR-ADM-06):
 * предпросмотр паспорта объекта в настраиваемом шаблоне без привязки
 * к формату (формат реестра определяется на этапе 2, ТЗ §5).
 */
export function RegistryPage() {
  const [enterpriseId, setEnterpriseId] = useState('');
  const [template, setTemplate] = useState<PassportTemplate>(readLocalTemplate);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldSource, setNewFieldSource] = useState('name');
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['admin', 'passport-templates'],
    queryFn: getPassportTemplates,
    retry: false,
  });

  useEffect(() => {
    const remote = templatesQuery.data?.[0];
    if (remote) {
      setTemplate(remote);
    }
  }, [templatesQuery.data]);

  const enterprisesQuery = useQuery({
    queryKey: ['admin', 'enterprises', 'all-for-registry'],
    queryFn: () => getEnterprisesForModeration(undefined),
  });

  const saveMutation = useMutation({
    mutationFn: (value: PassportTemplate) => savePassportTemplate(value),
    onSuccess: () => setError(null),
    onError: (cause) => {
      // Формат госреестра ещё не согласован — сохраняем шаблон локально.
      writeLocalTemplate(template);
      setError(errorMessage(cause, ru.common.unknownError));
    },
  });

  const enterprises = enterprisesQuery.data?.items ?? [];
  const enterprise = enterprises.find((item) => item.id === enterpriseId);

  const preview = useMemo(() => {
    if (!enterprise) return [];
    return template.fields.map((field) => ({
      label: field.label,
      value: readFieldValue(enterprise, field.source),
    }));
  }, [enterprise, template]);

  const filled = preview.filter((item) => item.value.trim().length > 0).length;
  const completeness = preview.length > 0 ? Math.round((filled / preview.length) * 100) : 0;

  const move = (index: number, direction: -1 | 1) => {
    setTemplate((previous) => {
      const fields = [...previous.fields];
      const target = index + direction;
      const source = fields[index];
      const destination = fields[target];
      if (!source || !destination) return previous;
      fields[index] = destination;
      fields[target] = source;
      return { ...previous, fields };
    });
  };

  const addField = () => {
    if (!newFieldLabel.trim()) return;
    setTemplate((previous) => ({
      ...previous,
      fields: [
        ...previous.fields,
        {
          id: `f-${Date.now()}`,
          source: newFieldSource,
          label: newFieldLabel.trim(),
        },
      ],
    }));
    setNewFieldLabel('');
  };

  const exportPassport = () => {
    if (!enterprise) return;
    const rows: CsvCell[][] = [
      [ru.registry.preview, enterprise.name],
      [],
      [ru.registry.field, ru.common.status],
      ...preview.map((item) => [item.label, item.value]),
    ];
    downloadCsv(`promtver-pasport-${enterprise.id}.csv`, rows);
  };

  return (
    <AppShell title={ru.registry.title} subtitle={ru.registry.subtitle}>
      <Card title={ru.registry.selectObject}>
        <Select
          label={ru.registry.selectObject}
          value={enterpriseId}
          onChange={setEnterpriseId}
          options={[
            { value: '', label: ru.registry.empty },
            ...enterprises.map((item) => ({ value: item.id, label: item.name })),
          ]}
        />
        <p className="text-xs text-muted">{ru.registry.templateHint}</p>
      </Card>

      <Card title={ru.registry.template}>
        <ul className="flex flex-col gap-2">
          {template.fields.map((field, index) => (
            <li key={field.id} className="rounded-card border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm">{field.label}</strong>
                <span className="font-mono text-xs text-muted">{field.source}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => move(index, -1)} disabled={index === 0}>
                  {ru.registry.moveUp}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => move(index, 1)}
                  disabled={index === template.fields.length - 1}
                >
                  {ru.registry.moveDown}
                </Button>
                <Button
                  variant="outline"
                  tone="danger"
                  onClick={() =>
                    setTemplate((previous) => ({
                      ...previous,
                      fields: previous.fields.filter((item) => item.id !== field.id),
                    }))
                  }
                >
                  {ru.registry.removeField}
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={ru.registry.field} value={newFieldLabel} onChange={setNewFieldLabel} />
          <Field label={ru.common.name} value={newFieldSource} onChange={setNewFieldSource} />
        </div>
        <Button tone="accent" onClick={addField} disabled={!newFieldLabel.trim()}>
          {ru.registry.addField}
        </Button>

        <Button
          variant="outline"
          disabled={saveMutation.isPending}
          onClick={() => {
            writeLocalTemplate(template);
            saveMutation.mutate(template);
          }}
        >
          {ru.common.save}
        </Button>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </Card>

      <Card title={ru.registry.preview}>
        <AsyncState
          isLoading={enterprisesQuery.isLoading}
          isError={enterprisesQuery.isError}
          isEmpty={!enterprise}
          emptyTitle={ru.registry.empty}
        >
          {enterprise ? (
            <>
              <KeyValue
                label={ru.registry.completeness}
                value={`${completeness} % (${filled} / ${preview.length})`}
                mono
              />
              <div className="flex flex-col gap-1">
                {preview.map((item) => (
                  <KeyValue
                    key={item.label}
                    label={item.label}
                    value={item.value || ru.registry.missingFields}
                  />
                ))}
              </div>
              <Button tone="accent" onClick={exportPassport}>
                {ru.registry.exportCsv}
              </Button>
            </>
          ) : null}
        </AsyncState>
      </Card>
    </AppShell>
  );
}
